import { useEffect, useMemo, useState } from "react";

// Returns a UI density scale based on device pixel ratio (DPR) and zoom.
// 1.00 for standard displays, ~1.15 for ~1.5x, ~1.25 for 2x and above.
export const useDensityScale = () => {
  const getScale = () => {
    if (typeof window === "undefined") return 1;
    const dpr = window.devicePixelRatio || 1;
    // Basic mapping; clamp to reasonable bounds
    let scale = 1;
    if (dpr >= 2.5) scale = 1.3;
    else if (dpr >= 2) scale = 1.25;
    else if (dpr >= 1.5) scale = 1.15;
    else scale = 1;
    return Math.max(1, Math.min(scale, 1.35));
  };

  const [scale, setScale] = useState(getScale);

  useEffect(() => {
    const handler = () => setScale(getScale());
    window.addEventListener("resize", handler);
    // Some browsers fire this when zoom level or pixel ratio changes.
    // Keep the same MediaQueryList reference for add/remove instead of
    // calling matchMedia() twice - matchMedia is not spec-guaranteed to
    // return the same object for the same query string.
    const mql = window.matchMedia
      ? window.matchMedia("(resolution: 2dppx)")
      : null;
    mql?.addEventListener("change", handler);
    return () => {
      window.removeEventListener("resize", handler);
      mql?.removeEventListener("change", handler);
    };
  }, []);

  // Memoize to avoid recalculations across renders
  return useMemo(() => scale, [scale]);
};
